import { VideoPlayerModal } from './VideoPlayerModal';
import { VimeoPlayerModal } from './VimeoPlayerModal';
import { InteractiveContentModal } from './InteractiveContentModal';
import { InfographicModal } from './InfographicModal';
import { LiaClassModal } from './LiaClassModal';
import { ReadingContentModal } from './ReadingContentModal';
import { ResourceContentModal } from './ResourceContentModal';

export type ContentType = 'video' | 'interactive' | 'reading' | 'audio' | 'infographic' | 'lia-class' | 'resource';

export interface ContentProgressUpdate {
  contentId: string;
  completed: boolean;
  progressPercent: number;
}

interface ContentLesson {
  id: string;
  title: string;
  duration: string;
  type: ContentType;
  description: string;
  completed: boolean;
  thumbnail: string;
  vimeo_player_url?: string | null;
  html_content?: string | null;
  url?: string | null;
  thumbnail_url?: string | null;
}

interface ContentPlayerModalProps {
  isOpen: boolean;
  onClose: () => void;
  lesson: ContentLesson | null;
  lessonIndex: number;
  onComplete: (lessonId: string) => void;
  onProgressSaved?: (progress: ContentProgressUpdate) => void;
}

export function ContentPlayerModal({
  isOpen,
  onClose,
  lesson,
  lessonIndex,
  onComplete,
  onProgressSaved
}: ContentPlayerModalProps) {
  if (!lesson) return null;

  const handleComplete = () => {
    onComplete(lesson.id);
  };

  // Video content — use Vimeo player if vimeo_player_url exists
  if (lesson.type === 'video') {
    if (lesson.vimeo_player_url) {
      return (
        <VimeoPlayerModal
          isOpen={isOpen}
          onClose={onClose}
          contentId={lesson.id}
          vimeoPlayerUrl={lesson.vimeo_player_url}
          title={lesson.title}
          onComplete={handleComplete}
          onProgressSaved={onProgressSaved as any}
          isCompleted={lesson.completed}
          contentDescription={lesson.description}
        />
      );
    }

    return (
      <VideoPlayerModal
        contentId={lesson.id}
        isOpen={isOpen}
        onClose={onClose}
        topic={{
          title: lesson.title,
          description: lesson.description,
          duration: lesson.duration,
          index: lessonIndex
        }}
        onComplete={() => handleComplete()}
        isViewed={lesson.completed}
      />
    );
  }

  // Reading content — dedicated article modal
  if (lesson.type === 'reading') {
    return (
      <ReadingContentModal
        isOpen={isOpen}
        onClose={onClose}
        lesson={{
          title: lesson.title,
          description: lesson.description,
          duration: lesson.duration
        }}
        htmlContent={lesson.html_content}
        contentId={lesson.id}
        onComplete={handleComplete}
        isCompleted={lesson.completed}
      />
    );
  }

  // Interactive/OVA content
  if (lesson.type === 'interactive') {
    return (
      <InteractiveContentModal
        contentId={lesson.id}
        isOpen={isOpen}
        onClose={onClose}
        lesson={{
          title: lesson.title,
          description: lesson.description,
          duration: lesson.duration
        }}
        htmlContent={lesson.html_content || undefined}
        contentUrl={lesson.url || undefined}
        onComplete={handleComplete}
        isCompleted={lesson.completed}
      />
    );
  }

  // Infographic content (also handles legacy audio)
  if (lesson.type === 'infographic') {
    return (
      <InfographicModal
        contentId={lesson.id}
        isOpen={isOpen}
        onClose={onClose}
        lesson={{
          title: lesson.title,
          description: lesson.description,
          duration: lesson.duration
        }}
        imageUrl={lesson.thumbnail_url || lesson.thumbnail || undefined}
        audioUrl={lesson.url || undefined}
        onComplete={handleComplete}
        isCompleted={lesson.completed}
      />
    );
  }

  // LIA Class content
  if (lesson.type === 'lia-class') {
    return (
      <LiaClassModal
        contentId={lesson.id}
        isOpen={isOpen}
        onClose={onClose}
        lesson={{
          title: lesson.title,
          description: lesson.description,
          duration: lesson.duration
        }}
        customPrompt={lesson.html_content || undefined}
        onComplete={handleComplete}
        isCompleted={lesson.completed}
      />
    );
  }

  // Resource content — PDF viewer or download
  if (lesson.type === 'resource') {
    return (
      <ResourceContentModal
        isOpen={isOpen}
        onClose={onClose}
        lesson={{
          title: lesson.title,
          description: lesson.description,
          duration: lesson.duration
        }}
        resourceUrl={lesson.url || undefined}
        contentId={lesson.id}
        onComplete={handleComplete}
        isCompleted={lesson.completed}
      />
    );
  }

  // Audio content — legacy fallback to infographic modal
  if (lesson.type === 'audio') {
    return (
      <InfographicModal
        contentId={lesson.id}
        isOpen={isOpen}
        onClose={onClose}
        lesson={{
          title: lesson.title,
          description: lesson.description,
          duration: lesson.duration
        }}
        audioUrl={lesson.url || undefined}
        onComplete={handleComplete}
        isCompleted={lesson.completed}
      />
    );
  }

  return null;
}
